import { ATHENS_GRAY } from "@/controls/shared/constants/colors";
import {
	ErrorModal,
	HelmetDexteel,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import type { Entry } from "@dexteel/mesf-core";
import { Grid2 as Grid, Paper, Typography } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StrandTimeSeriesData } from "../caster/models/StrandSpeed";
import {
	getBilletTempByProcessId,
	getMoldLevelsByProcessId,
	getStrandSpeedsByProcessId,
} from "../caster/repositories/ProductionRepository";
import { HeaderHeatDataReport } from "./components/HeaderHeatDataReport";
import { HeatReportTabs } from "./components/tabs/HeatReportTabs";
import { useHeatReportContext } from "./context/HeatReportContext";
import {
	Addition,
	CasterProcessData,
	Charge,
	EAFProcessData,
	HeatData,
	HeatReportDelay,
	Heating,
	Phase,
	Probe,
	Production,
	ScrapData,
	StrandData,
} from "./models/HeatReportModels";
import {
	getCASProcessReport,
	getEAFProcessReport,
	getEAFValuesByProcessId,
	getHeatReport,
	getHeatReportChemistrySamples,
} from "./repository/HeatReportRepository";

export const HeatReportPage = () => {
	const { heatId: heatIdParam, stage } = useParams<{
		heatId: string;
		stage: "eaf" | "caster" | "lab";
	}>();
	const navigate = useNavigate();
	const { state: utlSettingsState } = useUTLSettingsContext();
	const {
		state,
		state: { heat },
		actions: {
			setHeat,
			setEAFProcessData,
			setCASProcessData,
			setHeatLabData,
			setElementData,
			setAdditions,
			setCharges,
			setDelays,
			setLogbook,
			setProbes,
			setHeatings,
			setPhases,
			setProductionData,
			setScrapData,
			setStrandData,
			setIsDataLoading,
			setStrandSpeeds,
			setMoldLevels,
			setEAFValues,
			setBilletTemp,
			setEafChartOffsetMinutes,
		},
	} = useHeatReportContext();

	const HEAT_REPORT_EAF_CHART_OFFSET_MINUTES = Number(
		get(utlSettingsState.settings, "HEAT_REPORT_EAF_CHART_OFFSET_MINUTES", 40),
	);

	const [error, setError] = useState<string>("");

	// Set the configurable offset minutes in the context
	useEffect(() => {
		setEafChartOffsetMinutes(HEAT_REPORT_EAF_CHART_OFFSET_MINUTES);
	}, [HEAT_REPORT_EAF_CHART_OFFSET_MINUTES, setEafChartOffsetMinutes]);

	const getDataFromAPI = async (heatId: number | null, updateUrl = true) => {
		setIsDataLoading(true);
		const heatResp = await getHeatReport({ HeatId: heatId });
		if (heatResp.ok) {
			const heatData: HeatData = get(heatResp.data, "tables[0].rows[0]", {});
			setHeat(heatData);

			// Update URL with the loaded heat ID (if updateUrl is true)
			if (updateUrl && heatData.HeatId && stage) {
				navigate(`/heat-report/${heatData.HeatId}/${stage}`, {
					replace: true,
				});
			}
		} else {
			setError(heatResp.message);
		}

		setIsDataLoading(false);
	};

	const getProcessDataFromAPI = async () => {
		setIsDataLoading(true);

		const processId = stage === "eaf" ? heat.EAFProcessId : heat.CASProcessId;

		if (stage === "eaf" && processId) {
			const resp = await getEAFProcessReport({
				ProcessId: processId,
			});
			if (resp.ok) {
				const processData: EAFProcessData = get(
					resp.data,
					"tables[0].rows[0]",
					{},
				);
				const additions: Addition[] = get(resp.data, "tables[1].rows", []);
				const probes: Probe[] = get(resp.data, "tables[2].rows", []);
				const charges: Charge[] = get(resp.data, "tables[3].rows", []);
				const heatings: Heating[] = get(resp.data, "tables[4].rows", []);
				const phases: Phase[] = get(resp.data, "tables[5].rows", []);
				const delays: HeatReportDelay[] = get(resp.data, "tables[6].rows", []);
				const logbook: Entry[] = get(resp.data, "tables[7].rows", []);

				setEAFProcessData(processData);
				setAdditions(additions);
				setProbes(probes);
				setCharges(charges);
				setHeatings(heatings);
				setPhases(phases);
				setDelays(delays);
				setLogbook(logbook);
			} else {
				setError(resp.message);
			}
			const eafValuesResp = await getEAFValuesByProcessId({
				ProcessId: processId,
				OffsetMinutes: HEAT_REPORT_EAF_CHART_OFFSET_MINUTES,
			});
			if (eafValuesResp.ok) {
				const eafValues: StrandTimeSeriesData[] =
					eafValuesResp.data.tables[0].rows;
				setEAFValues(eafValues);
			} else {
				setError(eafValuesResp.message);
			}
		}
		if (stage === "caster" && processId) {
			const resp = await getCASProcessReport({
				ProcessId: processId,
			});
			if (resp.ok) {
				const processData: CasterProcessData = get(
					resp.data,
					"tables[0].rows[0]",
					{},
				);
				const strandData: StrandData[] = get(resp.data, "tables[1].rows", []);
				const probes: Probe[] = get(resp.data, "tables[2].rows", []);
				const productionData: Production[] = get(
					resp.data,
					"tables[3].rows",
					[],
				);
				const scrapData: ScrapData[] = get(resp.data, "tables[4].rows", []);
				const logbook: Entry[] = get(resp.data, "tables[5].rows", []);

				setCASProcessData(processData);
				setStrandData(strandData);
				setScrapData(scrapData);
				setProductionData(productionData);
				setProbes(probes);
				setLogbook(logbook);
			} else {
				setError(resp.message);
			}

			const strandSpeedResp = await getStrandSpeedsByProcessId(processId);
			const moldLevelsResp = await getMoldLevelsByProcessId(processId);
			const billetTempResp = await getBilletTempByProcessId(processId);
			if (strandSpeedResp.ok) {
				const strandSpeeds: StrandTimeSeriesData[] =
					strandSpeedResp.data.tables[0].rows;
				setStrandSpeeds(strandSpeeds);
			} else {
				setError(strandSpeedResp.message);
			}
			if (moldLevelsResp.ok) {
				const moldLevels: StrandTimeSeriesData[] =
					moldLevelsResp.data.tables[0].rows;
				setMoldLevels(moldLevels);
			} else {
				setError(moldLevelsResp.message);
			}
			if (billetTempResp.ok) {
				const billetTemp: StrandTimeSeriesData[] =
					billetTempResp.data.tables[0].rows;
				setBilletTemp(billetTemp);
			} else {
				setError(billetTempResp.message);
			}
		}
		if (stage === "lab" && heat.HeatId) {
			const resp = await getHeatReportChemistrySamples({ heatId: heat.HeatId });
			if (resp.ok) {
				setHeatLabData(resp.data.tables[0].rows[0]);
				setElementData(resp.data.tables[1].rows);
			} else {
				setError(resp.message);
			}
		}
		setIsDataLoading(false);
	};

	// Load heat based on URL parameter
	useEffect(() => {
		(async () => {
			if (heatIdParam === "latest" || !heatIdParam) {
				// Load latest heat
				await getDataFromAPI(null, true);
			} else {
				// Load specific heat by ID
				const numericHeatId = Number.parseInt(heatIdParam, 10);
				if (!Number.isNaN(numericHeatId)) {
					await getDataFromAPI(numericHeatId, false);
				}
			}
		})();
	}, [heatIdParam]);

	// Redirect to eaf stage if heat doesn't have Caster process and user is on caster stage
	useEffect(() => {
		if (
			heat.HeatId &&
			heat.HeatId > 0 &&
			stage === "caster" &&
			(!heat.CASProcessId || heat.CASProcessId === 0)
		) {
			navigate(`/heat-report/${heat.HeatId}/eaf`, { replace: true });
		}
	}, [heat.HeatId, heat.CASProcessId, stage, navigate]);

	// Load process data when heat or stage changes
	useEffect(() => {
		// Don't fetch process data until we have a valid heat loaded
		if (!heat.HeatId || heat.HeatId === 0 || !stage) {
			return;
		}

		(async () => {
			await getProcessDataFromAPI();
		})();
	}, [stage, heat.HeatId]);

	return (
		<>
			{heat && <HelmetDexteel title={`${heat.HeatName} - Heat Report`} />}
			<Paper
				style={{ backgroundColor: ATHENS_GRAY, border: "1px solid  #ccc" }}
			>
				<Grid container justifyContent="center" spacing={1}>
					<Grid size={{ xs: 11.8 }} container spacing={1}>
						<Grid size={{ xs: 12 }} sx={{ p: 1 }}>
							<Typography variant="h5" style={{ fontWeight: 600 }}>
								Heat Report
							</Typography>
						</Grid>
						<Grid size={{ xs: 12 }}>
							{heat && heat.HeatId > 0 && (
								<HeaderHeatDataReport
									heat={heat}
									getDataFromAPI={getDataFromAPI}
								/>
							)}
						</Grid>

						{/* Only render tabs (children) when heat (parent) is loaded */}
						{heat.HeatId > 0 ? (
							<Grid size={{ xs: 12 }}>
								<HeatReportTabs />
							</Grid>
						) : (
							<Grid size={{ xs: 12 }} container justifyContent="center">
								<Typography variant="body1" color="textSecondary">
									{state.isDataLoading
										? "Loading heat data..."
										: "No heat loaded. Please select a heat."}
								</Typography>
							</Grid>
						)}
					</Grid>
				</Grid>

				<ErrorModal error={error} onHide={() => setError("")} />
			</Paper>
		</>
	);
};
